/**
 * 核心函数：压缩单张图片至 1.9MB 以内并强制转为 JPG，同时约束最大长边为 1920
 * @param {File|String} fileOrUrl - 原始图片文件对象或网络URL
 * @return {Promise<Blob|File|String>} - 返回处理后的 Blob/File 或原网络字符串
 */
export function compressSingleImage(fileOrUrl, targetWidth = 1920, targetHeight = 1920) {
    return new Promise((resolve, reject) => {
        // 1. 🚀 完全对齐 Flutter：如果是网络图片，直接放行直通
        if (typeof fileOrUrl === 'string' && fileOrUrl.startsWith('https://api.antaressocial.com')) {
            return resolve(fileOrUrl);
        }

        const file = fileOrUrl;
        const MAX_SIZE = 1.9 * 1024 * 1024; // 1.9MB 阈值对齐 Flutter

        // 2. 🚀 检查尺寸约束：如果原本就小且是 JPEG，但如果分辨率高得离谱（比如 4000x4000），依然不能放行，必须重绘尺寸
        // 只有大小合规、格式合规，且不需要裁剪尺寸时，才完美直通
        if (file.size < MAX_SIZE && (file.type === 'image/jpeg' || file.type === 'image/jpg')) {
            // 这里我们先不读长宽，交由 Image.onload 判断，如果确实符合所有条件就直通
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                // 3. 🚀 尺寸等比缩放计算 (对齐 Flutter 的 minWidth/minHeight 约束)
                let width = img.width;
                let height = img.height;

                if (width > targetWidth || height > targetHeight) {
                    if (width > height) {
                        height = Math.round((height * targetWidth) / width);
                        width = targetWidth;
                    } else {
                        width = Math.round((width * targetHeight) / height);
                        height = targetHeight;
                    }
                }

                // 如果大小、格式、尺寸全都没变，直接放行原文件
                if (file.size < MAX_SIZE && (file.type === 'image/jpeg' || file.type === 'image/jpg') && width === img.width) {
                    console.log(`[直通] 图片完全合规，跳过压缩: ${file.name}`);
                    return resolve(file);
                }

                // 创建 Canvas
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = width;
                canvas.height = height;

                // 铺白底防止透明 PNG 转 JPG 变黑
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, width, height);

                // 根据文件体积智能判定初始画质 (完全对齐 Flutter：>10MB 用 75，否则用 80)
                let quality = file.size > 10 * 1024 * 1024 ? 0.75 : 0.80;
                let minQuality = 0.2;
                let maxQuality = 0.92;

                // 4. 阶梯式二分法压制体积
                const nextIteration = () => {
                    canvas.toBlob((blob) => {
                        if (!blob) return reject(new Error('Canvas toBlob 失败'));

                        if (blob.size <= MAX_SIZE || quality <= minQuality + 0.05) {
                            resolve(blob);
                        } else {
                            // 二分法降低画质
                            maxQuality = quality;
                            quality = (minQuality + maxQuality) / 2;
                            nextIteration();
                        }
                    }, 'image/jpeg', quality);
                };

                nextIteration();
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
    });
}

/**
 * 批量处理多张图片 (完全对齐 Flutter preProcessingMedia + compressImages 决策链)
 * @param {Array} fileList - 可能是 File 对象、包装对象、或者网络 URL 字符串的数组
 * @param {Function} onProgress - 进度回调 (current, total, percentage)
 * @return {Promise<Array>} - 返回处理好的 Blobs/Files/Urls 混合数组
 */
export async function compressMultipleImages(fileList, onProgress) {
    if (!fileList || fileList.length === 0) return [];

    // 💡 1. 统一清洗解包：不管上游是传 [{file}] 还是 [File]，统一还原
    const sanitizedFiles = fileList.map(item => {
        if (typeof item === 'string') return item; // 网络 URL
        return item.file || item; // 兼容包装类或原生 File
    }).filter(Boolean);

    // 💡 2. 🚀 全局扫描判定（对齐 Flutter 的 needsProcessingFiles 逻辑）
    let needsProcessing = false;
    const MAX_SIZE = 1.9 * 1024 * 1024;

    for (const item of sanitizedFiles) {
        if (typeof item === 'string' && item.startsWith('https://')) continue; // 网络图直接忽略

        // 只要有一张是非 JPEG 格式，或者体积超过 1.9MB，直接激活全局压缩流水线
        if (item.size >= MAX_SIZE || !(item.type === 'image/jpeg' || item.type === 'image/jpg')) {
            needsProcessing = true;
            break;
        }
    }

    // 💡 3. 策略调度
    const total = sanitizedFiles.length;
    const resultAssets = [];

    if (needsProcessing) {
        console.log(`⚠️ 检测到有图片体积超标(>=1.9MB)或格式不符，激活全渠道合规重绘流水线...`);

        for (let i = 0; i < total; i++) {
            if (onProgress) {
                const percentage = Math.round((i / total) * 100);
                onProgress(i, total, percentage);
            }

            const item = sanitizedFiles[i];
            // 压制并强制规范尺寸至 1920 (对齐 Flutter)
            const processed = await compressSingleImage(item, 1920, 1920);
            resultAssets.push(processed);
        }

        if (onProgress) onProgress(total, total, 100);
        return resultAssets;
    }

    // 💡 4. 如果全部完美合规，直接原封不动返回（对齐 Flutter 的 return files）
    console.log("🚀 所有图片均在 1.9MB 内且格式合规，触发秒级直通免压缩发布！");
    if (onProgress) onProgress(total, total, 100);
    return sanitizedFiles;
}